/**
 * Dashboard admin — clienți fidelizare.
 */
import { $, formatFirestoreDateRo, escapeHtml } from '../utils/helpers.js?v=82.86';
import { showSnackbar } from '../utils/ui.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';
import { db } from '../config/firebase.js?v=82.86';
import { getCurrentUser } from './auth.js?v=82.86';
import { getCurrentSettings, setCurrentSettings } from './settings.js?v=82.86';
import { getPublicClientSlug } from './public-client.js?v=82.86';
import { loadScriptOnce } from '../utils/lazy-loader.js?v=82.86';
import {
  listLoyaltyMembersBackend,
  awardLoyaltyRewardBackend,
  getLoyaltySettingsBackend,
  deleteLoyaltyMemberBackend,
  deleteAllLoyaltyDataBackend
} from './backend.js?v=82.86';

let loyaltySettingsLoadPromise = null;
let loyaltyMembersCache = [];

export function getLoyaltyMembersCache() {
  return loyaltyMembersCache;
}

export async function loadLoyaltyMembersCache() {
  try {
    const res = await listLoyaltyMembersBackend();
    loyaltyMembersCache = Array.isArray(res?.members) ? res.members : [];
  } catch (e) {
    logger.warn('[loadLoyaltyMembersCache]', e);
  }
  return loyaltyMembersCache;
}
let isDeletingAllLoyalty = false;

export function clearLoyaltySettingsLoadCache() {
  loyaltySettingsLoadPromise = null;
}

/** Încarcă setările fidelizare în memorie (tablet/admin); publicSites le are deja în snapshot. */
export async function ensureLoyaltySettingsLoaded(force = false) {
  const cur = getCurrentSettings();
  if (
    !force &&
    cur?.loyaltySettings &&
    typeof cur.loyaltySettings.active === 'boolean'
  ) {
    return true;
  }
  if (getPublicClientSlug()) {
    return !!cur?.loyaltySettings;
  }
  const user = getCurrentUser();
  if (!user) return false;

  if (loyaltySettingsLoadPromise) {
    await loyaltySettingsLoadPromise;
    return !!getCurrentSettings()?.loyaltySettings;
  }

  loyaltySettingsLoadPromise = (async () => {
    try {
      let settings = null;
      try {
        const snap = await db.doc(`merchants/${user.uid}/loyaltySettings/config`).get();
        if (snap.exists) settings = snap.data();
      } catch (e) {
        logger.warn('[ensureLoyaltySettingsLoaded] Firestore read failed:', e);
      }
      if (!settings) {
        const res = await getLoyaltySettingsBackend();
        settings = res?.settings || null;
      }
      if (settings) {
        const merged = { ...(getCurrentSettings() || {}), loyaltySettings: settings };
        setCurrentSettings(merged);
        return true;
      }
    } catch (e) {
      logger.warn('[ensureLoyaltySettingsLoaded]', e);
    }
    return false;
  })();

  const ok = await loyaltySettingsLoadPromise;
  loyaltySettingsLoadPromise = null;
  return ok;
}

function sourceLabel(source) {
  const raw = String(source || '').toLowerCase();
  if (raw === 'public') return 'Client public';
  if (raw === 'tablet') return 'Tabletă';
  if (raw === 'admin') return 'Admin';
  return source || '—';
}

function bindMemberRowActions(row, member, onRefresh) {
  const awardBtn = row.querySelector('.loyalty-award-btn');
  if (awardBtn) {
    awardBtn.addEventListener('click', async () => {
      awardBtn.disabled = true;
      try {
        await awardLoyaltyRewardBackend({ memberId: member.id });
        showSnackbar('Recompensa a fost marcată ca acordată.', 'success');
        await onRefresh();
      } catch (e) {
        showSnackbar(e?.message || 'Eroare la acordarea recompensei.', 'error');
        awardBtn.disabled = false;
      }
    });
  }

  const deleteBtn = row.querySelector('.loyalty-delete-member-btn');
  if (deleteBtn) {
    deleteBtn.addEventListener('click', async () => {
      if (!window.confirm(`Ștergi clientul ${member.email || ''} și toate bonurile lui?`)) return;
      deleteBtn.disabled = true;
      try {
        await deleteLoyaltyMemberBackend({ memberId: member.id });
        showSnackbar('Client șters.', 'success');
        await onRefresh();
      } catch (e) {
        showSnackbar(e?.message || 'Eroare la ștergere.', 'error');
        deleteBtn.disabled = false;
      }
    });
  }
}

function renderMemberRow(member, onRefresh) {
  const row = document.createElement('div');
  row.className = 'loyalty-dashboard-row';
  row.dataset.memberId = member.id || '';

  const receiptsList =
    Array.isArray(member.receipts) && member.receipts.length > 0
      ? member.receipts
          .map((r) => `<li>${escapeHtml(r.receiptNumber || '—')} <span class="smallnote">${formatFirestoreDateRo(r.createdAt)}</span></li>`)
          .join('')
      : '<li class="smallnote">Niciun bon</li>';

  const statusClass =
    member.rewardAvailable === true ? 'loyalty-status--reward' : 'loyalty-status--progress';

  row.innerHTML = `
    <div class="loyalty-dashboard-row__head">
      <strong>${escapeHtml(member.email || member.phoneMasked || member.phone || '—')}</strong>
      <span class="loyalty-status ${statusClass}">${escapeHtml(member.status || 'În progres')}</span>
    </div>
    <div class="loyalty-dashboard-row__meta">
      <span class="loyalty-dashboard-stat"><span class="loyalty-dashboard-stat__label">Progres</span> <strong>${member.progressX ?? 0}/${member.requiredPurchases ?? '—'}</strong></span>
      <span class="loyalty-dashboard-stat"><span class="loyalty-dashboard-stat__label">Achiziții</span> <strong>${member.purchaseCount ?? 0}</strong></span>
      <span class="loyalty-dashboard-stat"><span class="loyalty-dashboard-stat__label">Recompense</span> <strong>${member.rewardsClaimedCount ?? 0}</strong></span>
      <span class="loyalty-dashboard-stat loyalty-dashboard-stat--wide"><span class="loyalty-dashboard-stat__label">Ultima achiziție</span> <strong>${formatFirestoreDateRo(member.lastPurchaseAt)}</strong></span>
    </div>
    <details class="loyalty-dashboard-row__receipts">
      <summary>▶ Bonuri înregistrate (${(member.receipts || []).length})</summary>
      <ul>${receiptsList}</ul>
    </details>
    <div class="loyalty-dashboard-row__actions" style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px;">
      ${
        member.rewardAvailable
          ? `<button type="button" class="btn-primary loyalty-award-btn" data-member-id="${escapeHtml(member.id || '')}">Marchează recompensa ca acordată</button>`
          : ''
      }
      <button type="button" class="btn-danger loyalty-delete-member-btn" data-member-id="${escapeHtml(member.id || '')}">Șterge client</button>
    </div>
  `;

  bindMemberRowActions(row, member, onRefresh);
  return row;
}

export async function refreshLoyaltyDashboard() {
  const list = $('dashboardLoyaltyList');

  try {
    const res = await listLoyaltyMembersBackend();
    loyaltyMembersCache = Array.isArray(res?.members) ? res.members : [];

    if (list) {
      list.innerHTML = '<div class="dashboard-empty-state">Se încarcă...</div>';
      list.replaceChildren();

      if (loyaltyMembersCache.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'dashboard-empty-state';
        empty.textContent = 'Încă nu există clienți de fidelizare.';
        list.appendChild(empty);
      } else {
        loyaltyMembersCache.forEach((m) => {
          list.appendChild(renderMemberRow(m, refreshLoyaltyDashboard));
        });
      }
    }

    import('../ui/mod-client-modules.js?v=82.86')
      .then((m) => m.updateModClientModulesUI?.(getCurrentSettings()))
      .catch(() => {});
  } catch (e) {
    logger.warn('[refreshLoyaltyDashboard]', e);
    if (list) {
      list.innerHTML =
        '<p class="dashboard-empty-state dashboard-empty-state--error">Nu am putut încărca lista.</p>';
    }
  }
}

export async function exportLoyaltyMembers() {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');

  try {
    await loadScriptOnce('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js', 'XLSX', 8000);
    if (!window.XLSX) throw new Error('XLSX nu este disponibil');

    const res = await listLoyaltyMembersBackend();
    const members = Array.isArray(res?.members) ? res.members : [];
    if (members.length === 0) {
      showSnackbar('Niciun client de exportat');
      return;
    }

    const clientRows = [['Nr.', 'Email', 'Progres', 'Achiziții totale', 'Status', 'Recompense acordate', 'Ultima achiziție']];
    const receiptRows = [['Email', 'Nr. bon', 'Data', 'Sursă']];

    members.forEach((m, idx) => {
      clientRows.push([
        idx + 1,
        m.email || '',
        `${m.progressX ?? 0}/${m.requiredPurchases ?? '—'}`,
        m.purchaseCount ?? 0,
        m.status || '',
        m.rewardsClaimedCount ?? 0,
        formatFirestoreDateRo(m.lastPurchaseAt)
      ]);
      (m.receipts || []).forEach((r) => {
        receiptRows.push([
          m.email || '',
          r.receiptNumber || '',
          formatFirestoreDateRo(r.createdAt),
          sourceLabel(r.source)
        ]);
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(clientRows), 'Clienți');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(receiptRows), 'Bonuri');

    const filename = `fidelizare_${currentUser.uid}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    a.remove();

    showSnackbar('Excel descărcat.');
  } catch (e) {
    logger.error('[exportLoyaltyMembers]', e);
    showSnackbar('Eroare export: ' + (e?.message || 'necunoscută'), 'error');
  }
}

export function openDeleteAllLoyaltyModal() {
  const modal = document.getElementById('deleteAllLoyaltyModal');
  if (modal) modal.classList.add('active');
}

export function closeDeleteAllLoyaltyModal() {
  const modal = document.getElementById('deleteAllLoyaltyModal');
  if (modal) modal.classList.remove('active');
}

export function resetLoyaltyData() {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  openDeleteAllLoyaltyModal();
}

export async function confirmDeleteAllLoyaltyData() {
  if (isDeletingAllLoyalty) return;

  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');

  const btn = $('confirmDeleteAllLoyaltyBtn');
  const originalText = btn ? btn.textContent : 'Da, șterge tot';

  isDeletingAllLoyalty = true;
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.innerHTML = '<span class="inline-spinner"></span> Ștergere în curs...';
  }

  try {
    const result = await deleteAllLoyaltyDataBackend();
    closeDeleteAllLoyaltyModal();
    showSnackbar(result?.message || 'Date șterse.', 'success');
    await refreshLoyaltyDashboard();
    await refreshLoyaltyModalList();
  } catch (e) {
    logger.error('[confirmDeleteAllLoyaltyData]', e);
    showSnackbar('Eroare la ștergere: ' + (e?.message || 'necunoscută'), 'error');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
  } finally {
    isDeletingAllLoyalty = false;
  }
}

export function openLoyaltyMembersModal() {
  const modal = document.getElementById('loyaltyMembersModal');
  if (modal) {
    const h = modal.querySelector('h2');
    if (h) h.textContent = 'Clienți fidelizare';
    loyaltyFilter = 'all';
    loyaltyOpenReceipts = null;
    const actions = modal.querySelector('.sjc-actions');
    if (actions) actions.hidden = false;
    modal.classList.add('active');
    refreshLoyaltyModalList();
  }
}

export function closeLoyaltyMembersModal() {
  const modal = document.getElementById('loyaltyMembersModal');
  if (modal) modal.classList.remove('active');
}

let loyaltyFilter = 'all';
let loyaltyOpenReceipts = null;

function lyDate(v) {
  try {
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate();
    if (typeof v.toMillis === 'function') return new Date(v.toMillis());
    if (v.seconds || v._seconds) return new Date((v.seconds || v._seconds) * 1000);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch (_) { return null; }
}

function lyWhen(v) {
  const d = lyDate(v);
  if (!d) return '';
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const n = new Date();
  const today = new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime();
  const hm = d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  if (day === today) return `azi, ${hm}`;
  if (day === today - 864e5) return `ieri, ${hm}`;
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function lyEl(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

function renderLoyaltyCard(m, onRefresh) {
  const reward = m.rewardAvailable === true;
  const card = lyEl('article', `sjl-card${reward ? ' has-reward' : ''}`);
  const contact = String(m.email || m.phoneMasked || m.phone || '—');

  const head = lyEl('div', 'sjl-head');
  head.appendChild(lyEl('span', 'sjl-av', (contact[0] || '?').toUpperCase()));
  const txt = lyEl('div', 'sjl-head__text');
  txt.appendChild(lyEl('b', 'sjl-contact', contact));
  const bits = [`${m.purchaseCount ?? 0} ${(m.purchaseCount ?? 0) === 1 ? 'achiziție' : 'achiziții'}`];
  if (m.rewardsClaimedCount) bits.push(`${m.rewardsClaimedCount} ${m.rewardsClaimedCount === 1 ? 'recompensă primită' : 'recompense primite'}`);
  const last = lyWhen(m.lastPurchaseAt);
  if (last) bits.push(`ultima ${last}`);
  txt.appendChild(lyEl('span', 'sjl-sub', bits.join(' · ')));
  if (m.marketingSms) txt.appendChild(lyEl('span', 'sjl-mkt', 'Acceptă oferte prin SMS'));
  head.appendChild(txt);

  const del = lyEl('button', 'sjl-del');
  del.type = 'button';
  del.setAttribute('aria-label', `Șterge clientul ${contact}`);
  del.title = 'Șterge clientul';
  del.innerHTML = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16"/><path d="M10 11v6M14 11v6"/><path d="M6 7l1 13h10l1-13"/><path d="M9 7V4h6v3"/></svg>';
  del.addEventListener('click', async () => {
    if (!window.confirm(`Ștergi clientul ${m.email || ''} și toate bonurile lui?`)) return;
    del.disabled = true;
    try {
      await deleteLoyaltyMemberBackend({ memberId: m.id });
      showSnackbar('Client șters.', 'success');
      await onRefresh();
    } catch (e) {
      showSnackbar(e?.message || 'Eroare la ștergere.', 'error');
      del.disabled = false;
    }
  });
  head.appendChild(del);
  card.appendChild(head);

  const req = Math.max(0, Number(m.requiredPurchases) || 0);
  const x = Math.max(0, Number(m.progressX) || 0);
  const prog = lyEl('div', 'sjl-prog');
  const label = lyEl('div', 'sjl-prog__label');
  label.appendChild(lyEl('span', reward ? 'sjl-badge sjl-badge--reward' : 'sjl-badge', reward ? 'Recompensă disponibilă' : (m.status || 'În progres')));
  label.appendChild(lyEl('b', 'sjl-prog__num', req ? `${Math.min(x, req)} / ${req}` : `${x}`));
  prog.appendChild(label);
  if (req) {
    const bar = lyEl('div', 'sjl-bar');
    const fill = lyEl('span', 'sjl-bar__fill');
    fill.style.width = `${Math.min(100, Math.round((Math.min(x, req) / req) * 100))}%`;
    bar.appendChild(fill);
    prog.appendChild(bar);
  }
  card.appendChild(prog);

  const receipts = Array.isArray(m.receipts) ? m.receipts : [];
  const acts = lyEl('div', 'sjl-acts');
  if (reward) {
    const award = lyEl('button', 'sjl-award', 'Acordă recompensa');
    award.type = 'button';
    award.addEventListener('click', async () => {
      award.disabled = true;
      try {
        await awardLoyaltyRewardBackend({ memberId: m.id });
        showSnackbar('Recompensa a fost marcată ca acordată.', 'success');
        await onRefresh();
      } catch (e) {
        showSnackbar(e?.message || 'Eroare la acordarea recompensei.', 'error');
        award.disabled = false;
      }
    });
    acts.appendChild(award);
  }
  const open = loyaltyOpenReceipts === m.id;
  const rb = lyEl('button', 'sjl-mini', `${open ? 'Ascunde' : 'Vezi'} bonurile (${receipts.length})`);
  rb.type = 'button';
  rb.setAttribute('aria-expanded', open ? 'true' : 'false');
  rb.addEventListener('click', () => {
    loyaltyOpenReceipts = open ? null : m.id;
    renderLoyaltyModalCards();
  });
  acts.appendChild(rb);
  card.appendChild(acts);

  if (open) {
    const ul = lyEl('ul', 'sjl-receipts');
    if (!receipts.length) ul.appendChild(lyEl('li', 'sjl-receipts__empty', 'Niciun bon'));
    receipts.forEach((r) => {
      const li = lyEl('li');
      li.appendChild(lyEl('b', null, `Bon ${r.receiptNumber || '—'}`));
      li.appendChild(lyEl('span', null, [lyWhen(r.createdAt), sourceLabel(r.source)].filter((t) => t && t !== '—').join(' · ')));
      ul.appendChild(li);
    });
    card.appendChild(ul);
  }
  return card;
}

let loyaltyModalRefresh = null;

function renderLoyaltyModalCards() {
  const list = document.getElementById('loyaltyMembersModalList');
  if (!list) return;
  const all = loyaltyMembersCache || [];
  const withReward = all.filter((m) => m.rewardAvailable === true).length;
  const sub = document.getElementById('loyaltyMembersSub');
  if (sub) sub.textContent = all.length ? `${all.length} ${all.length === 1 ? 'client' : 'clienți'}${withReward ? ` · ${withReward} cu recompensă` : ''}` : '';
  const filters = document.getElementById('loyaltyMembersFilters');
  if (filters) {
    filters.hidden = all.length === 0;
    const labels = { all: `Toți · ${all.length}`, reward: `Recompensă · ${withReward}`, progress: `În progres · ${all.length - withReward}` };
    filters.querySelectorAll('[data-filter]').forEach((b) => {
      b.textContent = labels[b.dataset.filter];
      const on = b.dataset.filter === loyaltyFilter;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  list.replaceChildren();
  const modal = document.getElementById('loyaltyMembersModal');
  modal?.querySelector('.sjc-sheet')?.classList.toggle('is-compact', !all.length);
  const actions = modal?.querySelector('.sjc-actions');
  if (actions) actions.hidden = !all.length;
  if (!all.length) {
    const empty = lyEl('div', 'sjl-empty');
    empty.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18"/><path d="M7 15h4"/></svg>';
    empty.appendChild(lyEl('b', null, 'Încă nu există clienți de fidelizare'));
    empty.appendChild(lyEl('span', null, 'Apar aici după primul bon înregistrat.'));
    list.appendChild(empty);
    return;
  }
  const shown = all.filter((m) => (loyaltyFilter === 'reward' ? m.rewardAvailable === true : loyaltyFilter === 'progress' ? m.rewardAvailable !== true : true));
  if (!shown.length) {
    list.appendChild(lyEl('p', 'sjl-none', loyaltyFilter === 'reward' ? 'Nimeni nu are acum o recompensă de primit.' : 'Niciun client în progres.'));
    return;
  }
  // cei cu recompensă disponibilă primii
  shown.slice().sort((a, b) => (b.rewardAvailable === true) - (a.rewardAvailable === true)).forEach((m) => {
    list.appendChild(renderLoyaltyCard(m, loyaltyModalRefresh || (async () => {})));
  });
}

export async function refreshLoyaltyModalList() {
  const list = document.getElementById('loyaltyMembersModalList');
  if (!list) return;
  const filters = document.getElementById('loyaltyMembersFilters');
  if (filters && filters.dataset.bound !== '1') {
    filters.dataset.bound = '1';
    filters.addEventListener('click', (e) => {
      const b = e.target.closest('[data-filter]');
      if (!b) return;
      loyaltyFilter = b.dataset.filter;
      renderLoyaltyModalCards();
    });
  }
  list.innerHTML = '<div class="sjl-none">Se încarcă…</div>';
  await refreshLoyaltyDashboard();
  loyaltyModalRefresh = async () => {
    await refreshLoyaltyDashboard();
    renderLoyaltyModalCards();
  };
  renderLoyaltyModalCards();
}
